import { RefObject, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TableComponent } from '@ohpen/enso-ui';

import {
  buildFilterPatch,
  hasListUrlState,
  isListOnScreen,
  LIST_URL_SPECS,
  ListColumnFilters,
  ListUrlState,
  readListUrlState,
  writeListUrlState,
} from '@pmp/utils';

/**
 * The grid's change notification, as AntD reports it. Typed loosely on purpose: only the filter
 * selection is read here, and the handler has to stay assignable to any list's `Table` props.
 */
export type ListTableChange = (pagination: unknown, filters: ListColumnFilters, ...rest: unknown[]) => void;

/** Table props that keep the list's search and column filters in the query string. */
export interface ListUrlStateProps {
  onGlobalSearch?: (searchText: string) => void;
  onFiltersChange?: (filters: ListColumnFilters) => void;
  setActiveFilters?: (activeFilters: boolean) => void;
  onChange?: ListTableChange;
}

/**
 * Two-way sync between the query string and one list's grid state, so a filtered list is a URL: a
 * pasted link reproduces the view and back/forward work. Spread the result onto the enso Table.
 *
 * Search keeps enso's own `search-<tableId>` param, which the Table already applies when it
 * initialises and which PMP already links between lists with; this adds the write-back plus the
 * column filters. Applying the URL does not persist, so opening someone else's link does not become
 * the recipient's remembered state. A list with no entry in LIST_URL_SPECS gets no props at all and
 * behaves exactly as before.
 */
export const useListUrlState = (
  entityType: string | undefined,
  tableRef: RefObject<TableComponent | null>,
  columns?: readonly unknown[],
): ListUrlStateProps => {
  const [searchParams, setSearchParams] = useSearchParams();
  const registered = entityType === undefined ? undefined : LIST_URL_SPECS[entityType];

  // A list whose tab is not open shares its URL with the list that is, so it neither applies what the
  // query string says nor writes its own state into it: that is the other list's state right now.
  const spec = registered !== undefined && isListOnScreen(searchParams, registered) ? registered : undefined;

  useEffect(() => {
    const table = tableRef.current;
    if (spec === undefined || table === null) return;

    const state = table.getState();

    // Nothing in the URL means the view restored from storage stands. Writing an empty state over it
    // would clear it, so the restored view is serialized instead and what is on screen stays
    // copy-pasteable. Only when it changes something, or the write feeds itself.
    if (!hasListUrlState(searchParams, spec)) {
      const restored = { search: state.search.text ?? '', columnFilters: state.filters.byColumn };
      if (writeListUrlState(searchParams, spec, restored).toString() !== searchParams.toString()) {
        setSearchParams(previous => writeListUrlState(previous, spec, restored), { replace: true });
      }
      return;
    }

    const target = readListUrlState(searchParams, spec);

    // `commit: false`, so applying the URL does not report a search the user never typed.
    if ((state.search.text ?? '') !== target.search) table.setSearch(target.search, { commit: false });

    const columnKeys = [...state.columns.visible, ...state.columns.hidden].map(column => String(column.key));
    const patch = buildFilterPatch(target.columnFilters, state.filters.byColumn, columnKeys);
    if (Object.keys(patch).length > 0) {
      table.setFilters(patch, undefined, { persist: false });
      // The export reads AntD's filtered snapshot, which only refreshes on an AntD change. Dropping it
      // keeps the export off the rows the last column-filter interaction left behind.
      table.resetFilteredData();
    }
    // `columns` is a change signal, not data: a filter on a column that only arrives later (Products'
    // additional fields) is ignored on the first pass, and this brings it back once the column exists.
  }, [columns, searchParams, setSearchParams, spec, tableRef]);

  // Filter and search changes replace rather than push: the back button should leave the page, not
  // unwind every keystroke.
  const write = useCallback(
    (patch: Partial<ListUrlState>) => {
      if (spec === undefined) return;

      setSearchParams(previous => writeListUrlState(previous, spec, patch), { replace: true });
    },
    [setSearchParams, spec],
  );

  const onGlobalSearch = useCallback((searchText: string) => write({ search: searchText }), [write]);

  const onFiltersChange = useCallback((filters: ListColumnFilters) => write({ columnFilters: filters }), [write]);

  // A filter picked in a column header arrives here and nowhere else: enso's `onFiltersChange` prop is
  // its channel for lifting a sub-table's filters, so the main grid never calls it. Without this the
  // query string keeps whatever it had, and the next write applies that stale state back over the grid.
  const onChange = useCallback<ListTableChange>((_pagination, filters) => write({ columnFilters: filters }), [write]);

  // `onChange` covers the column dropdowns, but the Reset Filters button clears filters without it, and
  // this fires whenever the effective filter flag changes. It runs after the state it reports has
  // committed, so the snapshot is the applied one.
  const setActiveFilters = useCallback(() => {
    const table = tableRef.current;
    if (table === null) return;

    write({ columnFilters: table.getState().filters.byColumn });
  }, [tableRef, write]);

  return useMemo(
    () => (spec === undefined ? {} : { onGlobalSearch, onFiltersChange, setActiveFilters, onChange }),
    [onChange, onFiltersChange, onGlobalSearch, setActiveFilters, spec],
  );
};
